import PropTypes from 'prop-types';
import React from 'react';
import Link from 'next/link';

import {
  formatPhoneNumberFromNumber,
  formatStringToDisplayCase,
  formatDealerName,
} from 'pubweb-smokey/dist/utils/formatters';

import styles from './LearnMore.module.css';

const LearnMore = ({ dealerInfo }) => {
  const { dealer, brokeragePhone, brokerageEmail } = dealerInfo
    ? dealerInfo
    : {}; // destructure dealerInfo

  const storePhone = formatPhoneNumberFromNumber(dealer?.phoneNumber || '');
  const brokerPhone = formatPhoneNumberFromNumber(brokeragePhone || '');
  const brokerEmail = brokerageEmail || '';
  const year = new Date().getFullYear();

  // For dealer URL, see if it has www.
  const isSubdomain = (url) => {
    var regex = new RegExp(/^([a-z]+\:\/{2})?([\w-]+\.[\w-]+\.\w+)$/); // eslint-disable-line no-useless-escape
    return !!url.match(regex); // make sure it returns boolean
  };

  // Get a link and link text for a dealer URL.
  let dealerUrl = dealer?.url ? dealer.url.toLowerCase() : null;
  let dealerUrlText = '';

  if (dealerUrl) {
    //include a subdomain if missing
    dealerUrl = isSubdomain(dealerUrl)
      ? dealerUrl.trim()
      : 'www.' + dealerUrl.trim();

    // Save without https for link display
    dealerUrlText = dealerUrl;

    //disable https for IND dealers
    dealerUrl =
      dealer?.brandAcronym === 'IND'
        ? 'http://' + dealerUrl
        : 'https://' + dealerUrl;
  }

  return dealer ? (
    <div className={styles.learnMoreSection}>
      <h2 className={styles.heading}>Learn More About this Property</h2>

      <div className={styles.dealerName}>
        <h5>{formatDealerName(dealer?.dealerName || '')}</h5>
        {dealerUrl && <Link href={dealerUrl}>{dealerUrlText || ''}</Link>}
      </div>
      <div className={styles.storeInfoHours}>
        <div className={styles.storeInfo}>
          <h6 className={styles.storeInfoHeading}>Address</h6>
          <p className="caption">
            {formatStringToDisplayCase(dealer?.address1 || '')}
          </p>
          <p className="caption">
            {formatStringToDisplayCase(dealer?.city || '')},{' '}
            {dealer?.stateProvince || ''} {dealer?.postalCode}
          </p>
          <div className={styles.storePhone}>
            <h6 className={styles.storeInfoHeading}>Phone</h6>
            <a href={`tel:${storePhone}`}>{storePhone}</a>
          </div>
        </div>
        <div className={styles.storeHours}>
          <h6 className={styles.storeInfoHeading}>Hours</h6>
          <div className={styles.dayHours}>
            <div className={styles.days}>
              <p className="caption">Mon-Thur:</p>
              <p className="caption">Friday:</p>
              <p className="caption">Saturday:</p>
              <p className="caption">Sunday:</p>
            </div>
            <div className={styles.hours}>
              <p className="caption">{dealer?.weekdayHours || ''}</p>
              <p className="caption">{dealer?.fridayHours || ''}</p>
              <p className="caption">{dealer?.saturdayHours || ''}</p>
              <p className="caption">{dealer?.sundayHours || ''}</p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.brokerInfo}>
        {brokerPhone ? (
          <h5 className={styles.brokerInfoHeading}>
            Brokerage Phone:{' '}
            <a className={styles.brokerPhone} href={`tel:${brokerPhone}`}>
              {brokerPhone}
            </a>
            <br />
          </h5>
        ) : null}
        {brokerEmail ? (
          <h5 className={styles.brokerInfoHeading}>
            Listing Agent Email:{' '}
            <a className={styles.brokerEmail} href={`mailto:${brokerEmail}`}>
              {brokerEmail}
            </a>
            <br />
          </h5>
        ) : null}
      </div>
      <div className={styles.disclaimer}>
        <p className="xsmall">
          Copyright &copy; {year} Knoxville Area Association of Realtors &#174;.
          All rights reserved. All information provided by the listing
          agent/broker is deemed reliable but is not guaranteed and should be
          independently verified.
        </p>
      </div>
    </div>
  ) : null;
};

LearnMore.propTypes = {
  dealerInfo: PropTypes.shape({
    dealer: PropTypes.shape({
      dealerName: PropTypes.string,
      phoneNumber: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      stateProvince: PropTypes.string,
      postalCode: PropTypes.string,
      url: PropTypes.string,
      weekdayHours: PropTypes.string,
      fridayHours: PropTypes.string,
      saturdayHours: PropTypes.string,
      sundayHours: PropTypes.string,
      emailAddress: PropTypes.string,
      brandAcronym: PropTypes.string,
    }),
    brokerageEmail: PropTypes.string,
    brokeragePhone: PropTypes.string,
  }),
};

export default LearnMore;
